/**
 * Sidebar Component (Left Panel)
 * Manages Schema selection (unified dropdown), Levels, and template CRUD.
 */

import { useMemo, useCallback, useState, useEffect, useRef } from 'react';
import {
  useEditorContext,
  useSelectedAbstractNumId,
  useActiveLevel,
  useEditableMaxLevel,
} from '../../schema/hooks/useEditor';
import {
  getAbstractNumIds,
  ensureLevelStyle,
  ensureLevelInheritance,
  initializeSchema,
} from '../../schema/core/schemaManager';
import { getUserSchemas, saveSchema, deleteSchema as deleteSchemaApi, renameSchema as renameSchemaApi } from '../../services/editorSchema';
import { getToken } from '../../services/api';
import { useLanguage } from '../../i18nContext';
import type { EditorSchemaDTO } from '../../services/editorSchema';

const DEFAULT_MAX_LEVEL = 4;
const ABSOLUTE_MAX_LEVEL = 8;
const SELECTED_TEMPLATE_KEY = 'word-numbering-selected-template';
const DEFAULT_SCHEMA_ID_PREFIX = 'user_';

export interface SidebarTemplateInfo {
  type: 'user' | 'none';
  presetId: string;
  userSchemaId: string;
  userSchemaName: string;
}

export function Sidebar({ onActiveUserSchemaChange, onTemplateInfoChange, onUserSchemasChange, onApplyTemplate, onContextMenuRef, onTemplateChangeRef, appliedSchemaType, appliedSchemaId, hideTemplateSelect }: {
  onActiveUserSchemaChange?: (schemaId: string) => void;
  onTemplateInfoChange?: (info: SidebarTemplateInfo) => void;
  onUserSchemasChange?: (schemas: EditorSchemaDTO[]) => void;
  onApplyTemplate?: (schemaId: string) => void;
  onContextMenuRef?: React.MutableRefObject<((e: React.MouseEvent) => void) | null>;
  onTemplateChangeRef?: React.MutableRefObject<((value: string) => void) | null>;
  appliedSchemaType?: string;
  appliedSchemaId?: string;
  hideTemplateSelect?: boolean;
}) {
  const { state, dispatch } = useEditorContext();
  const { t } = useLanguage();
  const tc = t.templateCenter;
  const [selectedId, setSelectedId] = useSelectedAbstractNumId();
  const [activeLevel, setActiveLevel] = useActiveLevel();
  const [currentMaxLevel, setCurrentMaxLevel] = useEditableMaxLevel();
  const ids = useMemo(() => getAbstractNumIds(state.schema), [state.schema]);

  // User Schemas
  const [userSchemas, setUserSchemas] = useState<EditorSchemaDTO[]>([]);
  // Selected schema id (direct schemaId, no prefix)
  const [selectedValue, setSelectedValue] = useState('');
  // Context menu
  const [contextMenu, setContextMenu] = useState<{ x: number; y: number } | null>(null);
  // Rename modal
  const [renameTarget, setRenameTarget] = useState<{ schemaId: string; currentName: string } | null>(null);
  const [renameValue, setRenameValue] = useState('');
  const renameInputRef = useRef<HTMLInputElement>(null);
  // Create modal
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [createName, setCreateName] = useState('');
  const [creating, setCreating] = useState(false);
  const createInputRef = useRef<HTMLInputElement>(null);

  // Parse selectedValue into type + id
  const selectedType = useMemo(() => {
    if (selectedValue) return 'user' as const;
    return 'none' as const;
  }, [selectedValue]);

  const selectedPresetId = '';
  const selectedUserSchemaId = selectedValue;

  // Load data
  useEffect(() => {
    if (!getToken()) return;

    getUserSchemas().then(res => {
      const loadedUserSchemas = res || [];
      setUserSchemas(loadedUserSchemas);
      onUserSchemasChange?.(loadedUserSchemas);

      // 1. 优先按任务绑定的模板初始化下拉框
      if (appliedSchemaId) {
        if (loadedUserSchemas.find(s => s.schemaId === appliedSchemaId)) {
          setSelectedValue(appliedSchemaId);
          return;
        }
      }
      // 2. 默认选第一个模板（按 sort_order ASC 排序，预设模板排前面）
      if (loadedUserSchemas.length > 0) {
        setSelectedValue(loadedUserSchemas[0].schemaId);
      }
    }).catch(() => { });
  }, [appliedSchemaType, appliedSchemaId]);

  // Focus rename input
  useEffect(() => {
    if (renameTarget && renameInputRef.current) {
      renameInputRef.current.focus();
      renameInputRef.current.select();
    }
  }, [renameTarget]);

  // Focus create input
  useEffect(() => {
    if (showCreateModal && createInputRef.current) {
      createInputRef.current.focus();
    }
  }, [showCreateModal]);

  // Notify parent
  useEffect(() => {
    onActiveUserSchemaChange?.(selectedUserSchemaId);
  }, [selectedUserSchemaId, onActiveUserSchemaChange]);

  useEffect(() => {
    const info: SidebarTemplateInfo = {
      type: selectedType,
      presetId: selectedPresetId,
      userSchemaId: selectedUserSchemaId,
      userSchemaName: userSchemas.find(s => s.schemaId === selectedUserSchemaId)?.name || '',
    };
    onTemplateInfoChange?.(info);
  }, [selectedType, selectedPresetId, selectedUserSchemaId, userSchemas, onTemplateInfoChange]);

  // Parse schema data helper
  const parseSchemaData = useCallback((raw: { numberingSchemas: any; paragraphStyles?: any; editableMaxLevels?: any }) => {
    const numberingSchemas = typeof raw.numberingSchemas === 'string'
      ? JSON.parse(raw.numberingSchemas) : raw.numberingSchemas;
    const paragraphStyles = raw.paragraphStyles
      ? (typeof raw.paragraphStyles === 'string' ? JSON.parse(raw.paragraphStyles) : raw.paragraphStyles)
      : undefined;
    const editableMaxLevels = raw.editableMaxLevels
      ? (typeof raw.editableMaxLevels === 'string' ? JSON.parse(raw.editableMaxLevels) : raw.editableMaxLevels)
      : undefined;
    const data: any = { numberingSchemas };
    if (paragraphStyles) data.paragraphStyles = paragraphStyles;
    if (editableMaxLevels) data.editableMaxLevels = editableMaxLevels;
    return initializeSchema(data);
  }, []);

  // Unified template change handler
  const handleTemplateChange = useCallback((value: string) => {
    setSelectedValue(value);
    if (!value) return;

    const schema = userSchemas.find(s => s.schemaId === value);
    if (!schema) return;
    try {
      const schemaData = parseSchemaData(schema);
      dispatch({ type: 'RESET_SCHEMA', schema: schemaData });
      if (schema.selectedAbstractNumId != null) {
        setSelectedId(schema.selectedAbstractNumId);
      } else {
        const newIds = getAbstractNumIds(schemaData);
        if (newIds.length > 0) setSelectedId(newIds[0]);
      }
      // 自动应用：写入 localStorage + 通知父组件
      localStorage.setItem('word-numbering-schema', JSON.stringify(schemaData));
      localStorage.setItem('word-numbering-schema-activeId', String(schema.selectedAbstractNumId ?? 0));
      onApplyTemplate?.(value);
    } catch (err) {
      console.error('Failed to load schema:', err);
    }
  }, [userSchemas, parseSchemaData, dispatch, setSelectedId, onApplyTemplate]);

  // Expose handleTemplateChange to parent via ref
  useEffect(() => {
    if (onTemplateChangeRef) onTemplateChangeRef.current = handleTemplateChange;
  }, [handleTemplateChange, onTemplateChangeRef]);

  // Expose context menu trigger to parent via ref
  const handleContextMenuTrigger = useCallback((e: React.MouseEvent) => {
    setContextMenu({ x: e.clientX, y: e.clientY });
  }, []);
  useEffect(() => {
    if (onContextMenuRef) onContextMenuRef.current = handleContextMenuTrigger;
  }, [handleContextMenuTrigger, onContextMenuRef]);

  // Rename
  const handleStartRename = useCallback(() => {
    setContextMenu(null);
    if (!selectedValue) return;
    const schema = userSchemas.find(s => s.schemaId === selectedValue);
    if (!schema) return;
    setRenameTarget({ schemaId: schema.schemaId, currentName: schema.name });
    setRenameValue(schema.name);
  }, [selectedValue, userSchemas]);

  const handleConfirmRename = useCallback(() => {
    if (!renameTarget || !renameValue.trim()) return;
    renameSchemaApi(renameTarget.schemaId, renameValue.trim())
      .then(() => {
        setUserSchemas(prev => prev.map(s =>
          s.schemaId === renameTarget.schemaId ? { ...s, name: renameValue.trim() } : s
        ));
        setRenameTarget(null);
      })
      .catch(err => console.error('Rename failed:', err));
  }, [renameTarget, renameValue]);

  // Delete
  const handleDelete = useCallback(() => {
    setContextMenu(null);
    if (!selectedValue) return;
    if (!window.confirm(tc.confirmDelete)) return;
    deleteSchemaApi(selectedValue)
      .then(() => {
        const remaining = userSchemas.filter(s => s.schemaId !== selectedValue);
        setUserSchemas(remaining);
        onUserSchemasChange?.(remaining);
        if (remaining.length > 0) {
          handleTemplateChange(remaining[0].schemaId);
        } else {
          setSelectedValue('');
        }
      })
      .catch(err => console.error('Delete failed:', err));
  }, [selectedValue, userSchemas, onUserSchemasChange, handleTemplateChange, tc.confirmDelete]);

  // Create
  const handleOpenCreate = useCallback(() => {
    setContextMenu(null);
    setCreateName('');
    setShowCreateModal(true);
  }, []);

  const handleConfirmCreate = useCallback(() => {
    if (!createName.trim() || creating) return;
    setCreating(true);
    saveSchema({
      name: createName.trim(),
      numberingSchemas: state.schema.numberingSchemas,
      paragraphStyles: state.schema.paragraphStyles,
      editableMaxLevels: state.editableMaxLevels,
      selectedAbstractNumId: state.selectedAbstractNumId,
    })
      .then(res => {
        setShowCreateModal(false);
        setCreating(false);
        if (res) {
          const updated = [res, ...userSchemas];
          setUserSchemas(updated);
          onUserSchemasChange?.(updated);
          setSelectedValue(res.schemaId);
        }
      })
      .catch(err => {
        console.error('Create template failed:', err);
        setCreating(false);
      });
  }, [createName, creating, state.schema, state.editableMaxLevels, state.selectedAbstractNumId, userSchemas, onUserSchemasChange]);

  // Level Handlers
  const canAddMore = currentMaxLevel < ABSOLUTE_MAX_LEVEL;
  const canRemove = currentMaxLevel > DEFAULT_MAX_LEVEL;

  const handleAddLevel = useCallback(() => {
    if (!canAddMore) return;
    const newMaxLevel = currentMaxLevel + 1;
    let newSchema = ensureLevelStyle(state.schema, state.selectedAbstractNumId, newMaxLevel);
    newSchema = ensureLevelInheritance(newSchema, state.selectedAbstractNumId, newMaxLevel);
    dispatch({ type: 'RESET_SCHEMA', schema: newSchema });
    setCurrentMaxLevel(newMaxLevel);
    setActiveLevel(newMaxLevel);
  }, [canAddMore, currentMaxLevel, state.schema, state.selectedAbstractNumId, dispatch, setActiveLevel, setCurrentMaxLevel]);

  const handleRemoveLevel = useCallback(() => {
    if (!canRemove) return;
    const newMaxLevel = currentMaxLevel - 1;
    let newSchema = ensureLevelInheritance(state.schema, state.selectedAbstractNumId, newMaxLevel);
    dispatch({ type: 'RESET_SCHEMA', schema: newSchema });
    setCurrentMaxLevel(newMaxLevel);
    if (activeLevel > newMaxLevel) {
      setActiveLevel(newMaxLevel);
    }
  }, [canRemove, currentMaxLevel, activeLevel, state.schema, state.selectedAbstractNumId, dispatch, setActiveLevel, setCurrentMaxLevel]);

  return (
    <div className="app-sidebar">
      {/* Schema Selection - only if not rendered in header */}
      {!hideTemplateSelect && (
        <div className="sidebar-section template-section">
          <div className="section-title">{tc.schema}</div>
          <div className="user-schema-select-row">
            <select
              className="template-select"
              value={selectedValue}
              onChange={e => handleTemplateChange(e.target.value)}
            >
              <option value="" hidden></option>
              {userSchemas.map(s => (
                <option key={s.schemaId} value={s.schemaId}>{s.name}</option>
              ))}
            </select>
            {getToken() && (
              <button
                className="icon-btn user-schema-menu-btn"
                onClick={e => setContextMenu({ x: e.clientX, y: e.clientY })}
                title={tc.templateActions}
              >⋮</button>
            )}
          </div>
        </div>
      )}

      {/* Context menu */}
      {contextMenu && (
        <>
          <div className="menu-backdrop" onClick={() => setContextMenu(null)} />
          <div className="dropdown-menu" style={{ position: 'fixed', left: contextMenu.x, top: contextMenu.y, zIndex: 1000, width: 'fit-content', minWidth: 0 }}>
            <button onClick={handleOpenCreate}>{tc.newTemplate}</button>
            {selectedValue && (
              <>
                <div className="divider" />
                <button onClick={handleStartRename}>{tc.rename}</button>
                {userSchemas.length > 1 && (
                  <button className="danger" onClick={handleDelete}>{tc.delete}</button>
                )}
              </>
            )}
          </div>
        </>
      )}

      {/* Rename modal */}
      {renameTarget && (
        <div className="rename-modal-backdrop" onClick={() => setRenameTarget(null)}>
          <div className="rename-modal" onClick={e => e.stopPropagation()}>
            <div className="rename-modal-title">{tc.renameTitle}</div>
            <input
              ref={renameInputRef}
              className="rename-modal-input"
              value={renameValue}
              onChange={e => setRenameValue(e.target.value)}
              onKeyDown={e => { if (e.key === 'Enter') handleConfirmRename(); if (e.key === 'Escape') setRenameTarget(null); }}
              placeholder={tc.inputPlaceholder}
            />
            <div className="rename-modal-actions">
              <button className="rename-modal-cancel" onClick={() => setRenameTarget(null)}>{tc.cancel}</button>
              <button className="rename-modal-confirm" onClick={handleConfirmRename} disabled={!renameValue.trim()}>{tc.confirm}</button>
            </div>
          </div>
        </div>
      )}

      {/* Create modal */}
      {showCreateModal && (
        <div className="rename-modal-backdrop" onClick={() => setShowCreateModal(false)}>
          <div className="rename-modal" onClick={e => e.stopPropagation()}>
            <div className="rename-modal-title">{tc.createTitle}</div>
            <input
              ref={createInputRef}
              className="rename-modal-input"
              value={createName}
              onChange={e => setCreateName(e.target.value)}
              onKeyDown={e => { if (e.key === 'Enter') handleConfirmCreate(); if (e.key === 'Escape') setShowCreateModal(false); }}
              placeholder={tc.inputPlaceholder}
            />
            <div className="rename-modal-actions">
              <button className="rename-modal-cancel" onClick={() => setShowCreateModal(false)}>{tc.cancel}</button>
              <button className="rename-modal-confirm" onClick={handleConfirmCreate} disabled={!createName.trim() || creating}>
                {creating ? tc.creating : tc.create}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Level List */}
      <div className="sidebar-section level-section">
        <div className="section-title">{tc.levels}</div>
        <div className="level-list">
          {Array.from({ length: currentMaxLevel + 1 }, (_, i) => {
            return (
              <button
                key={i}
                className={`level-item ${activeLevel === i ? 'active' : ''}`}
                onClick={() => setActiveLevel(i)}
              >
                <span className="level-number">{tc.levelPrefix} {i + 1}</span>
              </button>
            );
          })}
        </div>

        <div className="level-actions">
          {canAddMore && (
            <button className="add-level-btn" onClick={handleAddLevel}>
              + {tc.addLevel}
            </button>
          )}
          {canRemove && (
            <button className="remove-level-btn" onClick={handleRemoveLevel}>
              - {tc.removeLevel}
            </button>
          )}
        </div>
      </div>

    </div>
  );
}
